import { TemplateDTO } from "@retrospecter/shared/types";
import { accentFor } from "./Board/columnAccents";
import { useTranslation } from "../i18n";

interface TemplatePreviewProps {
  template: TemplateDTO;
  /** Column guidance is worth reading before choosing, but not on a tiny card. */
  showHints?: boolean;
}

/** How many ghost notes each column gets, cycled so the board looks lived-in. */
const GHOST_NOTES = [3, 2, 3, 2, 3];

/**
 * A miniature of the board a template produces.
 *
 * The names of a format say very little on their own — "Starfish" or "4 Ls"
 * tell a facilitator nothing about the shape of the board they are about to put
 * in front of their team. This shows the columns, in their real accent colours
 * and real order, with the guidance each one carries.
 */
export function TemplatePreview({ template, showHints = false }: TemplatePreviewProps) {
  const { columnTitle, columnHint } = useTranslation();

  return (
    <div
      aria-hidden="true"
      className="flex gap-1.5 overflow-hidden rounded-lg bg-surface-sunken/60 p-1.5"
    >
      {template.columns.map((column, index) => {
        const accent = accentFor(index);
        const ghosts = GHOST_NOTES[index % GHOST_NOTES.length];

        return (
          <div
            key={column.id}
            className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-line bg-surface"
          >
            <div className={`h-[3px] ${accent.bar}`} />

            <div className="min-w-0 px-1.5 pb-1.5 pt-1">
              {/* Wrapped, not truncated: "Wind (helps us)" and "Keep doing"
                  lose their meaning when cut to "Wind (help…". */}
              <p className="line-clamp-2 break-words text-[0.6rem] font-bold uppercase leading-tight tracking-[0.04em] text-ink-soft">
                {columnTitle(column.title, template.name)}
              </p>

              {showHints && (
                <p className="mt-0.5 line-clamp-2 text-[0.6rem] leading-tight text-ink-faint">
                  {columnHint(column.title, column.hint, template.name)}
                </p>
              )}

              <div className="mt-1.5 space-y-1">
                {Array.from({ length: ghosts }).map((_, note) => (
                  <div
                    key={note}
                    className={`h-2.5 rounded-sm border-l-2 bg-surface-sunken ${accent.noteBorder}`}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
